import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Alert, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import {
  ActivityDetail,
  ActivityOverview,
  OverviewTrainee,
  SessionInspect as SessionInspectData,
  TimelineItem,
  fetchTraineeActivityDetail,
  fetchTraineeDevplanState,
  fetchTraineeSessionInspect,
} from './api';
import SessionInspect from './SessionInspect';
import {
  SOLO_STATUS_LABELS,
  formatWhen,
  soloLabel,
} from './helpers';
import type { DevPlanState } from '../TraineeDashboard/DevelopmentPlan/types';
import DevPlanTreePreview from './DevPlanTreePreview';
import SoloGradingCard from './SoloGradingCard';
import TraineeTimeline, {
  TIMELINE_LIMIT,
  isInspectableItem,
  timelineItemTitle,
} from './TraineeTimeline';

// Timeline item types whose inspect view is the trainee's development plan
// (solo IDG rooms included: tree centered on their node + grading overlay).
const DEVPLAN_ITEM_TYPES = [
  'attempt_scenario',
  'attempt_ladder',
  'attempt_game',
  'node_unlocked',
  'solo_session',
];

import './clientStudio.css';

/** Fill the viewport below the component's own top edge, so the rail and the
 *  timeline scroll internally while the tab frame stays put (studio2 pattern). */
const useFillHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(440, window.innerHeight - top - 24));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return { ref, height };
};

const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  plainValue?: boolean;
}> = ({ label, value, sub, plainValue }) => (
  <div className="cstudio__tile">
    <span className="cstudio__tile-label">{label}</span>
    <span
      className={`cstudio__tile-value ${
        plainValue ? 'cstudio__tile-value--plain' : ''
      }`}
    >
      {value}
    </span>
    {sub != null && <span className="cstudio__tile-sub">{sub}</span>}
  </div>
);

/** The same seven metrics as the Trainees roster table, as a KPI stat bar. */
const StatBar: React.FC<{ trainee: OverviewTrainee }> = ({ trainee }) => {
  const { sessions, devplan } = trainee;
  const scenario = devplan.attempts.scenario;
  const solo = devplan.solo_sessions;
  return (
    <div className="cstudio__tiles">
      <Tile
        label="Assessment sessions"
        value={`${sessions.played}/${sessions.provisioned}`}
        sub={
          sessions.deals
            ? `played / provisioned · ${sessions.deals} deals`
            : 'played / provisioned'
        }
      />
      <Tile
        label="Development plan"
        value={
          devplan.level === null
            ? '—'
            : `${devplan.nodes_owned}/${devplan.nodes_total}`
        }
        sub={devplan.level === null ? 'not started' : 'nodes owned'}
      />
      <Tile
        label="Node ranks"
        value={scenario?.count ? `${scenario.passed}/${scenario.count}` : '—'}
        sub={scenario?.count ? 'attempts passed' : undefined}
      />
      <Tile label="Warm-ups done" value={devplan.ladders_done || '—'} />
      <Tile label="Mini Games" value={devplan.games_played || '—'} />
      <Tile
        label="Mini sessions"
        value={solo.launched ? `${solo.passed}/${solo.launched}` : '—'}
        sub={
          solo.latest
            ? `${soloLabel(solo.latest)} — ${SOLO_STATUS_LABELS[solo.latest.status]}`
            : undefined
        }
      />
      <Tile
        label="Last action"
        value={trainee.last_action ? formatWhen(trainee.last_action) : '—'}
        plainValue
      />
    </div>
  );
};

type Props = {
  overview: ActivityOverview;
  klassId: number;
  plan?: string;
};

const StudioView: React.FC<Props> = ({ overview, klassId, plan }) => {
  const { ref, height } = useFillHeight();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(
    overview.trainees[0]?.user_id ?? null,
  );
  const [detail, setDetail] = useState<ActivityDetail>();
  const [error, setError] = useState<string>();
  const [waiting, setWaiting] = useState(false);
  // The timeline item under inspection; cleared whenever a new detail loads.
  const [inspected, setInspected] = useState<TimelineItem | null>(null);
  // The trainee's real skill tree (observer devplan_state), fetched lazily on
  // the first devplan-item click and cached until the trainee/plan changes.
  const [devplanState, setDevplanState] = useState<DevPlanState | null>(null);
  const [devplanError, setDevplanError] = useState<string>();
  // Finished-session drill-down, fetched per clicked session item.
  const [sessionInspect, setSessionInspect] = useState<SessionInspectData | null>(null);
  const [sessionError, setSessionError] = useState<string>();

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return overview.trainees;
    return overview.trainees.filter(
      t =>
        t.display_name.toLowerCase().includes(q) ||
        t.username.toLowerCase().includes(q),
    );
  }, [overview.trainees, search]);

  // Keep the selection on a real roster member (klass/plan switches).
  const selected =
    overview.trainees.find(t => t.user_id === selectedId) ??
    visible[0] ??
    overview.trainees[0];

  useEffect(() => {
    if (!selected) return;
    let active = true;
    setWaiting(true);
    setError(undefined);

    fetchTraineeActivityDetail(klassId, selected.user_id, plan)
      .then(result => {
        if (active) {
          setDetail(result);
          // Default the inspect card to the newest inspectable item.
          setInspected(result.timeline.find(isInspectableItem) ?? null);
          setDevplanState(null);
          setDevplanError(undefined);
        }
      })
      .catch(requestError => {
        if (active)
          setError(
            requestError instanceof Error
              ? requestError.message
              : 'The trainee activity detail could not be loaded.',
          );
      })
      .finally(() => {
        if (active) setWaiting(false);
      });

    return () => {
      active = false;
    };
  }, [klassId, selected?.user_id, plan]);

  // Lazy-load the real skill tree the first time a devplan item is inspected.
  const wantsTree =
    inspected !== null && DEVPLAN_ITEM_TYPES.includes(inspected.type);

  // What the tree should center on: the item's node; for a mini game, the first
  // node serving it (primary link preferred) — and the game's card as overlay.
  const inspectFocus = useMemo(() => {
    if (!wantsTree || !inspected || !devplanState)
      return { node: null as string | null, game: null as string | null };
    if (inspected.type === 'attempt_game') {
      const key = inspected.asset_key ?? null;
      const links = key
        ? devplanState.node_games.filter(g => g.asset_key === key)
        : [];
      const link = links.find(g => g.role === 'primary') ?? links[0];
      return { node: link?.node_code ?? null, game: key };
    }
    return { node: inspected.node_internal_code ?? null, game: null };
  }, [wantsTree, inspected, devplanState]);
  useEffect(() => {
    if (!wantsTree || devplanState || !selected) return;
    let active = true;
    setDevplanError(undefined);
    fetchTraineeDevplanState(klassId, selected.user_id, plan)
      .then(result => {
        if (active) setDevplanState(result);
      })
      .catch(requestError => {
        if (active)
          setDevplanError(
            requestError instanceof Error
              ? requestError.message
              : 'The development plan could not be loaded.',
          );
      });
    return () => {
      active = false;
    };
  }, [wantsTree, devplanState, klassId, selected?.user_id, plan]);

  // Finished assessment sessions drill into the per-session report; solo IDG
  // rooms go through the tree view above instead.
  const wantsSession =
    inspected !== null &&
    inspected.type === 'session_finished' &&
    typeof inspected.session_id === 'number';
  // Keyed on the inspected OBJECT (each detail load builds fresh items), so a
  // trainee switch always refetches — even when the newly auto-selected item
  // points at the SAME shared session id as the previous trainee's (an id-based
  // dependency deadlocked here: the detail load cleared the data, but no dep
  // changed, so the spinner never resolved).
  useEffect(() => {
    if (!wantsSession || !selected || !inspected) return;
    const sessionId = inspected.session_id as number;
    let active = true;
    setSessionError(undefined);
    setSessionInspect(null);
    fetchTraineeSessionInspect(klassId, selected.user_id, sessionId)
      .then(result => {
        if (active) setSessionInspect(result);
      })
      .catch(requestError => {
        if (active)
          setSessionError(
            requestError instanceof Error
              ? requestError.message
              : 'The session details could not be loaded.',
          );
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspected, selected?.user_id, klassId]);

  if (!selected) {
    return <Alert type="info" showIcon message="No trainees in this class." />;
  }

  return (
    <div ref={ref} className="cstudio" style={{ height }}>
      <div className="cstudio__rail">
        <div className="cstudio__rail-head">
          <span className="cstudio__rail-title">
            {overview.klass.name} — Roster
          </span>
        </div>
        <div className="cstudio__rail-toolbar">
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search trainees"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="cstudio__rail-list">
          {visible.map(trainee => (
            <div
              key={trainee.user_id}
              className={`cstudio__rail-row ${
                trainee.user_id === selected.user_id
                  ? 'cstudio__rail-row--active'
                  : ''
              }`}
              onClick={() => setSelectedId(trainee.user_id)}
            >
              <div style={{ minWidth: 0 }}>
                <div className="cstudio__rail-name">{trainee.display_name}</div>
                <div className="cstudio__rail-sub">
                  {trainee.last_action
                    ? `last action ${formatWhen(trainee.last_action)}`
                    : 'no activity yet'}
                </div>
              </div>
              <span className="cstudio__rail-metric">
                {trainee.devplan.nodes_total
                  ? `${trainee.devplan.nodes_owned}/${trainee.devplan.nodes_total}`
                  : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="cstudio__workspace">
        <div className="cstudio__player-header">
          <span className="cstudio__player-name">{selected.display_name}</span>
          {selected.display_name !== selected.username && (
            <span className="cstudio__player-sub">{selected.username}</span>
          )}
        </div>

        <StatBar trainee={selected} />

        <div className="cstudio__panel">
          <div className="cstudio__panel-card cstudio__panel-card--timeline">
            <div className="cstudio__panel-card-head">
              <span className="cstudio__panel-card-title">
                Timeline
                {detail && detail.timeline.length > TIMELINE_LIMIT
                  ? ` (latest ${TIMELINE_LIMIT} of ${detail.timeline.length})`
                  : ''}
              </span>
            </div>
            <div className="cstudio__panel-card-body cstudio__panel-card-body--tinted">
              {error && (
                <Alert
                  type="error"
                  showIcon
                  message={error}
                  style={{ marginBottom: 12 }}
                />
              )}
              {waiting && !detail ? (
                <Spin />
              ) : (
                detail && (
                  <TraineeTimeline
                    timeline={detail.timeline}
                    selectedItem={inspected}
                    onSelectItem={setInspected}
                  />
                )
              )}
            </div>
          </div>

          <div className="cstudio__panel-card cstudio__panel-card--inspect">
            <div className="cstudio__panel-card-head">
              <span className="cstudio__panel-card-title">Inspect</span>
              {inspected && (
                <span className="cstudio__inspect-head-item">
                  <strong>{timelineItemTitle(inspected)}</strong>{' '}
                  <span className="cstudio__tile-sub">
                    {formatWhen(inspected.timestamp)}
                  </span>
                </span>
              )}
              {inspected?.session_id != null && (
                // Subtle dev helper: the underlying PM session id.
                <span className="cstudio__inspect-head-session">
                  session #{inspected.session_id}
                </span>
              )}
            </div>
            {inspected && wantsTree ? (
              // Devplan items (node ranks / warm-ups / mini games) inspect as
              // the trainee's REAL skill tree — the same canvas they play on.
              <div className="cstudio__panel-card-body cstudio__panel-card-body--tree">
                {devplanError && (
                  <Alert
                    type="error"
                    showIcon
                    message={devplanError}
                    style={{ margin: 12 }}
                  />
                )}
                {devplanState ? (
                  <DevPlanTreePreview
                    plan={devplanState}
                    focusInternalCode={inspectFocus.node}
                    gameAssetKey={inspectFocus.game}
                    bottomLeftOverlay={
                      inspected.type === 'solo_session' ? (
                        <SoloGradingCard item={inspected} />
                      ) : undefined
                    }
                  />
                ) : (
                  !devplanError && <Spin style={{ margin: 16 }} />
                )}
              </div>
            ) : inspected && wantsSession ? (
              // Assessment session -> per-player session report. The body does
              // not scroll; each column inside is its own scroll container.
              <div className="cstudio__panel-card-body cstudio__panel-card-body--session">
                {sessionError && (
                  <Alert type="error" showIcon message={sessionError} />
                )}
                {sessionInspect ? (
                  <SessionInspect data={sessionInspect} />
                ) : (
                  !sessionError && <Spin />
                )}
              </div>
            ) : (
              <div className="cstudio__panel-card-body">
                {!inspected && (
                  <span className="cstudio__inspect-empty">
                    Click a timeline item to inspect it.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioView;
